import React from 'react';
import { FOCUS_RING } from './focusRing';

export interface TabItem {
    key: string;
    label: React.ReactNode;
    disabled?: boolean;
}

export interface TabsProps {
    items: TabItem[];
    activeKey: string;
    onChange: (key: string) => void;
    trailing?: React.ReactNode;
    className?: string;
    style?: React.CSSProperties;
}

const Tabs: React.FC<TabsProps> = ({
    items,
    activeKey,
    onChange,
    trailing,
    className = '',
    style,
}) => {
    const tabs = (
        <>
            {items.map((item) => {
                const isActive = item.key === activeKey;
                const colorClass = item.disabled
                    ? 'text-[var(--malachite-text-dimmed)] cursor-not-allowed'
                    : isActive
                      ? 'text-[var(--malachite-accent-foreground)]'
                      : 'text-[var(--malachite-text-secondary)] cursor-pointer hover:text-[var(--malachite-accent-foreground)]';

                return (
                    <button
                        key={item.key}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        disabled={item.disabled}
                        onClick={() => {
                            if (!item.disabled) {
                                onChange(item.key);
                            }
                        }}
                        // 8 (pt) + 16 (leading-4 label) + 14 (pb) + 2 (the underline) = 40, the height the design…
                        className={`relative bg-transparent border-0 px-0 pt-2 pb-[14px] -mb-px text-[13px] leading-4 font-medium tracking-[0.13px] whitespace-nowrap transition-colors duration-150 ${FOCUS_RING} ${colorClass}`}
                        style={{
                            borderBottom: isActive
                                ? '2px solid var(--malachite-accent-foreground)'
                                : '2px solid transparent',
                        }}
                    >
                        {item.label}
                    </button>
                );
            })}
        </>
    );

    if (!trailing) {
        return (
            <div
                role="tablist"
                className={`flex items-stretch gap-5 border-b border-solid border-[var(--malachite-card-border)] ${className}`}
                style={style}
            >
                {tabs}
            </div>
        );
    }

    return (
        <div className="flex items-stretch border-b border-solid border-[var(--malachite-card-border)]">
            {/* `className`/`style` stay on the tablist in both branches, so a consumer's spacing
                still spaces tab against tab and a `[role=tablist]` selector still finds them. */}
            <div
                role="tablist"
                className={`flex items-stretch gap-5 shrink-0 ${className}`}
                style={style}
            >
                {tabs}
            </div>
            <div className="flex min-w-0 flex-1 items-center justify-end pl-4">{trailing}</div>
        </div>
    );
};

export default Tabs;
